import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const methodOptions = [
  'stir',
  'shake',
  'build',
  'rolling',
  'shake & build',
  'shake & double strain',
] as const;

type MethodOption = (typeof methodOptions)[number];

type IngredientRow = {
  id: string;
  name: string;
  amount: string;
};

type RecipeFormState = {
  recipeName: string;
  author: string;
  method: MethodOption | '';
  ingredients: IngredientRow[];
  homemadeNote: string;
  contact: string;
};

const createIngredientRow = (): IngredientRow => ({
  id: `${Date.now()}-${Math.random().toString(36)?.slice?.(2, 8) ?? 'item'}`,
  name: '',
  amount: '',
});

const initialFormState = (): RecipeFormState => ({
  recipeName: '',
  author: '',
  method: '',
  ingredients: [createIngredientRow()],
  homemadeNote: '',
  contact: '',
});

const fieldClassName =
  'w-full rounded-[16px] border border-white/12 bg-black/45 px-4 py-3 text-sm leading-6 text-white outline-none transition-all placeholder:text-white/28 focus:border-[#FFC000] focus:ring-2 focus:ring-[#FFC000]/20';

export const RecipeForm: React.FC = () => {
  const [form, setForm] = useState<RecipeFormState>(() => initialFormState());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const filledIngredientCount = useMemo(() => {
    return (form?.ingredients ?? []).filter((item) => {
      const name = item?.name?.trim?.() ?? '';
      const amount = item?.amount?.trim?.() ?? '';
      return Boolean(name || amount);
    }).length;
  }, [form?.ingredients]);

  const updateField = (field: keyof Omit<RecipeFormState, 'ingredients'>, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const updateIngredient = (id: string, field: keyof Omit<IngredientRow, 'id'>, value: string) => {
    setForm((prev) => ({
      ...prev,
      ingredients: (prev?.ingredients ?? []).map((item) => {
        if ((item?.id ?? '') !== id) {
          return item;
        }

        return {
          ...item,
          [field]: value,
        };
      }),
    }));

    setErrors((prev) => ({
      ...prev,
      ingredients: '',
    }));
  };

  const addIngredientRow = () => {
    setForm((prev) => ({
      ...prev,
      ingredients: [...(prev?.ingredients ?? []), createIngredientRow()],
    }));
  };

  const removeIngredientRow = (id: string) => {
    setForm((prev) => {
      const nextIngredients = (prev?.ingredients ?? []).filter((item) => (item?.id ?? '') !== id);

      return {
        ...prev,
        ingredients: nextIngredients.length > 0 ? nextIngredients : [createIngredientRow()],
      };
    });
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};
    const recipeName = form?.recipeName?.trim?.() ?? '';
    const author = form?.author?.trim?.() ?? '';
    const method = form?.method?.trim?.() ?? '';
    const ingredients = form?.ingredients ?? [];

    if (!recipeName) {
      nextErrors.recipeName = '请输入配方名称';
    }

    if (!author) {
      nextErrors.author = '请输入作者名称';
    }

    if (!method) {
      nextErrors.method = '请选择出品方式';
    }

    const validIngredients = ingredients.filter((item) => {
      const name = item?.name?.trim?.() ?? '';
      const amount = item?.amount?.trim?.() ?? '';
      return Boolean(name && amount);
    });

    if (validIngredients.length === 0) {
      nextErrors.ingredients = '请至少填写一组完整的材料与比例';
    }

    const halfFilledIngredient = ingredients.some((item) => {
      const name = item?.name?.trim?.() ?? '';
      const amount = item?.amount?.trim?.() ?? '';
      return Boolean((name && !amount) || (!name && amount));
    });

    if (halfFilledIngredient) {
      nextErrors.ingredients = '每一行材料都需要同时填写材料名称和用量';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors ?? {}).length === 0;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    window.setTimeout(() => {
      toast.success('感谢提交！我们会尽快审核你的配方。');
      setForm(initialFormState());
      setErrors({});
      setSubmitting(false);
    }, 400);
  };

  return (
    <section className="bg-[#0a0a0a] px-5 pb-24 md:px-10 md:pb-28">
      <div className="mx-auto max-w-[1200px]">
        <div className="rounded-[28px] border border-[#FFC000]/18 bg-[linear-gradient(180deg,rgba(255,192,0,0.08),rgba(255,255,255,0.02)_18%,rgba(255,255,255,0.015)_100%)] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.34)] md:p-10">
          <div className="mb-10 max-w-3xl">
            <div className="mb-4 inline-block border border-[#FFC000]/60 px-4 py-1 text-[11px] tracking-[0.35em] text-[#FFC000]">
              配方投稿
            </div>
            <h2 className="mb-4 font-['Noto_Serif_SC','Songti_SC',serif] text-3xl text-white md:text-5xl">
              提交你的配方
            </h2>
            <div className="text-sm leading-7 text-white/70 md:text-base md:leading-8">
              欢迎提交你以元酒梦为灵感创作的鸡尾酒配方。我们会根据配方完整度、创意方向与品牌契合度进行审核。
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-3">
                <label htmlFor="recipeName" className="block text-sm tracking-[0.18em] text-white/78">
                  配方名称 <span className="text-[#FFC000]">*</span>
                </label>
                <input
                  id="recipeName"
                  type="text"
                  value={form?.recipeName ?? ''}
                  onChange={(event) => updateField('recipeName', event?.target?.value ?? '')}
                  placeholder="请输入鸡尾酒名称"
                  className={fieldClassName}
                  disabled={submitting}
                />
                {errors?.recipeName ? <div className="text-sm text-[#f4c94d]">{errors.recipeName}</div> : null}
              </div>

              <div className="space-y-3">
                <label htmlFor="author" className="block text-sm tracking-[0.18em] text-white/78">
                  作者 <span className="text-[#FFC000]">*</span>
                </label>
                <input
                  id="author"
                  type="text"
                  value={form?.author ?? ''}
                  onChange={(event) => updateField('author', event?.target?.value ?? '')}
                  placeholder="请输入创作者名称"
                  className={fieldClassName}
                  disabled={submitting}
                />
                {errors?.author ? <div className="text-sm text-[#f4c94d]">{errors.author}</div> : null}
              </div>
            </div>

            <div className="space-y-3">
              <label htmlFor="method" className="block text-sm tracking-[0.18em] text-white/78">
                出品方式 <span className="text-[#FFC000]">*</span>
              </label>
              <select
                id="method"
                value={form?.method ?? ''}
                onChange={(event) => updateField('method', event?.target?.value ?? '')}
                className={`${fieldClassName} appearance-none`}
                disabled={submitting}
              >
                <option value="" className="bg-[#111111] text-white/40">
                  请选择出品方式
                </option>
                {methodOptions.map((option) => (
                  <option key={option} value={option} className="bg-[#111111] text-white">
                    {option}
                  </option>
                ))}
              </select>
              {errors?.method ? <div className="text-sm text-[#f4c94d]">{errors.method}</div> : null}
            </div>

            <div className="space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-sm tracking-[0.18em] text-white/78">
                    材料与比例 <span className="text-[#FFC000]">*</span>
                  </div>
                  <div className="mt-2 text-sm text-white/45">
                    每行填写一组材料与用量，例如：元酒2021 / 45ml
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addIngredientRow}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#FFC000]/40 px-4 py-2 text-sm text-[#FFC000] transition-colors hover:bg-[#FFC000] hover:text-black"
                  disabled={submitting}
                >
                  <Plus className="h-4 w-4" />
                  添加一行
                </button>
              </div>

              <div className="space-y-4">
                {(form?.ingredients ?? []).map((item, index) => (
                  <div
                    key={item?.id ?? index}
                    className="grid gap-3 rounded-[18px] border border-white/10 bg-black/30 p-4 md:grid-cols-[1.2fr_0.7fr_auto] md:items-center"
                  >
                    <div className="space-y-2">
                      <label htmlFor={`ingredient-name-${item?.id ?? index}`} className="block text-xs tracking-[0.16em] text-white/48">
                        材料名称
                      </label>
                      <input
                        id={`ingredient-name-${item?.id ?? index}`}
                        type="text"
                        value={item?.name ?? ''}
                        onChange={(event) => updateIngredient(item?.id ?? '', 'name', event?.target?.value ?? '')}
                        placeholder="例如：元酒2021"
                        className={fieldClassName}
                        disabled={submitting}
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor={`ingredient-amount-${item?.id ?? index}`} className="block text-xs tracking-[0.16em] text-white/48">
                        用量
                      </label>
                      <input
                        id={`ingredient-amount-${item?.id ?? index}`}
                        type="text"
                        value={item?.amount ?? ''}
                        onChange={(event) => updateIngredient(item?.id ?? '', 'amount', event?.target?.value ?? '')}
                        placeholder="例如：45ml / 2 dash"
                        className={fieldClassName}
                        disabled={submitting}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => removeIngredientRow(item?.id ?? '')}
                      className="inline-flex h-12 items-center justify-center gap-2 rounded-[14px] border border-white/10 bg-white/5 px-4 text-sm text-white/70 transition-colors hover:border-[#FFC000]/40 hover:text-[#FFC000]"
                      disabled={submitting}
                      aria-label={`删除第 ${index + 1} 行材料`}
                    >
                      <Trash2 className="h-4 w-4" />
                      删除
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3 text-sm text-white/45">
                <div>已填写 {filledIngredientCount} 组材料</div>
                <div>支持毫升、dash、片、top 等表达</div>
              </div>
              {errors?.ingredients ? <div className="text-sm text-[#f4c94d]">{errors.ingredients}</div> : null}
            </div>

            <div className="space-y-3">
              <label htmlFor="homemadeNote" className="block text-sm tracking-[0.18em] text-white/78">
                自制材料说明
              </label>
              <textarea
                id="homemadeNote"
                value={form?.homemadeNote ?? ''}
                onChange={(event) => updateField('homemadeNote', event?.target?.value ?? '')}
                placeholder="如有自制材料，请填写制作方法"
                className={`${fieldClassName} min-h-[140px] resize-y`}
                disabled={submitting}
              />
            </div>

            <div className="space-y-3">
              <label htmlFor="contact" className="block text-sm tracking-[0.18em] text-white/78">
                联系方式
              </label>
              <input
                id="contact"
                type="text"
                value={form?.contact ?? ''}
                onChange={(event) => updateField('contact', event?.target?.value ?? '')}
                placeholder="请输入邮箱或微信，方便联系"
                className={fieldClassName}
                disabled={submitting}
              />
            </div>

            <div className="flex flex-col gap-4 border-t border-white/10 pt-6 md:flex-row md:items-center md:justify-between">
              <div className="text-sm leading-7 text-white/45">
                提交前请确认配方名称、出品方式与材料比例填写完整，我们会尽快进行内容审核。
              </div>
              <button
                type="submit"
                className="inline-flex min-w-[180px] items-center justify-center rounded-full bg-[#FFC000] px-7 py-3.5 text-sm font-semibold tracking-[0.18em] text-black transition-colors hover:bg-[#d9a400] disabled:cursor-not-allowed disabled:bg-[#80671a] disabled:text-black/65"
                disabled={submitting}
              >
                {submitting ? '提交中' : '提交配方'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RecipeForm;
